import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { BYGNING, type EtasjeDef } from '@/data/building';
import { TIDSKOSTNADER } from '@/data/timeCosts';
import { erIHeisen, npcIRom } from '@/data/schedules';
import { finnNpc } from '@/data/npcs';
import {
  FISKENDE_AVSLORT,
  FISKENDE_HINTS,
  HEIS_MOTE_INTRO,
  HEIS_MOTE_VALG,
  KONFRONTASJON,
  KONFRONTASJON_LUKK_LABEL,
  type HeisDialogValg,
} from '@/data/rivalDialog';
import { initialerAv } from '@/data/portretter';
import { gameState } from '@/state/store';
import { gameClock } from '@/systems/gameClock';
import { rivalen } from '@/systems/rival';
import { sfx } from '@/systems/sfx';
import { haptisk } from '@/systems/haptisk';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';

const HUD_HOYDE = 100;

// Tverrsnitt-layout (Mad TV): hele bygget synlig, sjakt til venstre.
const BYGG_TOPP = 150;
const ETASJE_HOYDE = 176;
const BYGG_X = 70;
const BYGG_BREDDE = GAME_WIDTH - 140;
const SJAKT_X = BYGG_X + 24;
const SJAKT_BREDDE = 180;
const INNHOLD_X = SJAKT_X + SJAKT_BREDDE + 28;

export interface HeisSceneData {
  etasjeNr: number;
}

/**
 * Heisen (fase 2 + Mad TV-tverrsnitt): hele Forlagshuset vises som
 * snitt — 9 etasjestriper med navn og hvem som er der akkurat nå,
 * og heissjakta med kabinen som kjører mellom etasjene. Tapp en
 * etasje for å dra dit (5 min/etasje fra data/timeCosts.ts).
 * Rival-møtene (5B/5D) skjer i kabinen.
 */
export class HeisScene extends Phaser.Scene {
  private naaEtasje = 5;
  private kjorer = false;
  private kabin?: Phaser.GameObjects.Container;
  private kabinTekst?: Phaser.GameObjects.Text;

  constructor() {
    super('Heis');
  }

  init(data: HeisSceneData): void {
    this.naaEtasje = data?.etasjeNr ?? 5;
    this.kjorer = false;
  }

  create(): void {
    this.tegnBakgrunn();
    this.tegnBygning();
    this.tegnKabin();

    // 5B: står rivalen i heisen? Bare i heisvinduene fra timeplanen
    // (08:30–09:00 og 12:00–12:30), 40 % sjanse — eller garantert
    // konfrontasjon hvis spilleren nettopp signerte fra stallen (5D).
    const iVindu = erIHeisen('kapelle-rival', gameClock.naa, gameState.dag);
    const utfall = rivalen.avgjorHeismote(iVindu);
    if (utfall !== 'ingen') {
      this.visHeismote(utfall);
    }
  }

  // ---------- Tverrsnittet ----------

  private tegnBakgrunn(): void {
    // Oslo-himmel bak bygget, mørkere grunn under kjelleren.
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0xb8d4e3, 1).setOrigin(0, 0);
    const byggBunn = BYGG_TOPP + BYGNING.length * ETASJE_HOYDE;
    this.add
      .rectangle(0, byggBunn, GAME_WIDTH, GAME_HEIGHT - byggBunn, 0x4a3826, 1)
      .setOrigin(0, 0);
    // Takkant med litt sliten sjarm.
    this.add
      .rectangle(BYGG_X - 14, BYGG_TOPP - 18, BYGG_BREDDE + 28, 18, 0x8b5e3c, 1)
      .setOrigin(0, 0);
    this.add
      .text(GAME_WIDTH / 2, HUD_HOYDE + 8, 'Forlagshuset', {
        fontFamily: FONTS.display,
        fontSize: fontStr(30),
        color: '#1a1a1a',
      })
      .setOrigin(0.5, 0);
  }

  private tegnBygning(): void {
    BYGNING.forEach((etasje, i) => {
      this.tegnEtasjeStripe(etasje, BYGG_TOPP + i * ETASJE_HOYDE);
    });

    // Sjakta tegnes over stripene: mørk søyle med skinner.
    const sjaktH = BYGNING.length * ETASJE_HOYDE;
    this.add
      .rectangle(SJAKT_X, BYGG_TOPP, SJAKT_BREDDE, sjaktH, 0x1a1a1a, 0.82)
      .setOrigin(0, 0);
    this.add
      .rectangle(SJAKT_X + 14, BYGG_TOPP, 4, sjaktH, 0xb8923f, 0.5)
      .setOrigin(0, 0);
    this.add
      .rectangle(SJAKT_X + SJAKT_BREDDE - 18, BYGG_TOPP, 4, sjaktH, 0xb8923f, 0.5)
      .setOrigin(0, 0);
  }

  private tegnEtasjeStripe(etasje: EtasjeDef, y: number): void {
    const erHer = etasje.nr === this.naaEtasje;
    const erKD = etasje.nr === 7;
    const erTak = etasje.nr === 8;
    const erKjeller = etasje.nr === 0;

    const bgFarge = erKD ? 0x2c3a52 : erTak ? 0xdfeaf2 : erKjeller ? 0x5c4630 : COLORS.beige;
    const tekstFarge = erKD || erKjeller ? '#f4ead5' : '#1a1a1a';

    const bg = this.add
      .rectangle(BYGG_X, y, BYGG_BREDDE, ETASJE_HOYDE, bgFarge, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(erHer ? 4 : 2, erHer ? COLORS.ochre : 0x8b5e3c, erHer ? 1 : 0.5);

    // Etasjeskille (dekke/gulv) i bunnen av stripa.
    this.add
      .rectangle(BYGG_X, y + ETASJE_HOYDE - 6, BYGG_BREDDE, 6, 0x6b5236, 0.8)
      .setOrigin(0, 0);

    this.add
      .text(INNHOLD_X, y + 26, `${etasjeTegn(etasje.nr)}  ${etasje.navn}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: erKD ? '#dfe3e8' : tekstFarge,
      })
      .setOrigin(0, 0);

    const status = erHer ? 'Du er her' : this.kostnadTekst(etasje.nr);
    this.add
      .text(INNHOLD_X, y + 76, status, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: erHer ? '#862736' : erKD ? '#9aa3ad' : '#8B5E3C',
        fontStyle: erHer ? '700' : '400',
      })
      .setOrigin(0, 0);

    this.tegnTilstedevaerelse(etasje, y);

    if (!erHer) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, erKD ? 0.5 : 0.9));
      bg.on('pointerout', () => bg.setFillStyle(bgFarge, 1));
      bg.on('pointerup', () => this.kjorTil(etasje));
    }
  }

  /**
   * Mad TV-magien: du ser fra sjakta hvem som er hvor i bygget akkurat
   * nå (timeplanene i data/schedules.ts) — og kan planlegge dagen.
   */
  private tegnTilstedevaerelse(etasje: EtasjeDef, y: number): void {
    const tilstede = etasje.rom
      .flatMap((rom) => npcIRom(rom, gameClock.naa, gameState.dag))
      .filter((n) => !gameState.harFlag(`stall_signert_${n.id}`));
    tilstede.slice(0, 3).forEach((npc, i) => {
      const x = BYGG_X + BYGG_BREDDE - 70 - i * 96;
      const senterY = y + ETASJE_HOYDE / 2 - 14;
      const pKey = npc.portrettKey ?? '';
      if (pKey && this.textures.exists(pKey)) {
        const img = this.add.image(x, senterY, pKey).setOrigin(0.5);
        const tex = this.textures.get(pKey).getSourceImage();
        const h = (tex as HTMLImageElement).height || 1;
        img.setScale(96 / h);
      } else {
        const ring = this.add.circle(x, senterY, 34, COLORS.white, 1);
        ring.setStrokeStyle(3, COLORS.wine, 0.9);
        this.add
          .text(x, senterY, initialerAv(npc.navn), {
            fontFamily: FONTS.display,
            fontSize: fontStr(20),
            color: '#862736',
          })
          .setOrigin(0.5);
      }
      this.add
        .text(x, senterY + 52, fornavnAv(npc.navn), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#3a2e22',
          backgroundColor: 'rgba(253,250,243,0.85)',
          padding: { left: 5, right: 5, top: 2, bottom: 2 },
        })
        .setOrigin(0.5, 0);
    });
  }

  private tegnKabin(): void {
    const kabinB = SJAKT_BREDDE - 44;
    const kabinH = ETASJE_HOYDE - 48;
    const kabin = this.add.container(SJAKT_X + SJAKT_BREDDE / 2, this.etasjeSenterY(this.naaEtasje));
    const boks = this.add
      .rectangle(0, 0, kabinB, kabinH, 0x5c4630, 1)
      .setOrigin(0.5)
      .setStrokeStyle(4, 0xb8923f, 1);
    this.kabinTekst = this.add
      .text(0, 0, etasjeTegn(this.naaEtasje), {
        fontFamily: FONTS.display,
        fontSize: fontStr(44),
        color: '#f4d58d',
      })
      .setOrigin(0.5);
    kabin.add([boks, this.kabinTekst]);
    kabin.setDepth(5);
    this.kabin = kabin;
  }

  private etasjeSenterY(nr: number): number {
    const indeks = BYGNING.findIndex((e) => e.nr === nr);
    return BYGG_TOPP + indeks * ETASJE_HOYDE + ETASJE_HOYDE / 2;
  }

  private kostnadTekst(nr: number): string {
    const avstand = Math.abs(nr - this.naaEtasje);
    return `${avstand * TIDSKOSTNADER.heisPerEtasje} min`;
  }

  /**
   * Kjører heisen: kabinen sklir gjennom sjakta til valgt etasje
   * (1-2 sek), så trekkes tiden og spilleren lander i korridoren.
   */
  private kjorTil(etasje: EtasjeDef): void {
    if (this.kjorer) return;
    this.kjorer = true;
    haptisk.lett();
    sfx.play('click');

    const fra = this.naaEtasje;
    const til = etasje.nr;
    const avstand = Math.abs(til - fra);

    const ankommet = (): void => {
      sfx.play('bell');
      gameClock.brukTid(
        avstand * TIDSKOSTNADER.heisPerEtasje,
        `heis til ${etasje.navn}`,
      );
      // Tok heisturen resten av dagen, har dagsoppsummeringen allerede
      // vekket rom-scenen — ikke start korridoren over den.
      if (gameClock.dagenErOver) return;
      this.scene.start('Korridor', { etasjeNr: til, ankomstViaHeis: true });
    };

    if (foretrekkerRedusertBevegelse() || !this.kabin) {
      this.kabinTekst?.setText(etasjeTegn(til));
      ankommet();
      return;
    }

    this.tweens.add({
      targets: this.kabin,
      y: this.etasjeSenterY(til),
      duration: Math.min(2000, 380 + avstand * 240),
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        // Kabin-displayet viser nærmeste etasje mens den kjører.
        const naermest = this.naermesteEtasjeNr(this.kabin!.y);
        this.kabinTekst?.setText(etasjeTegn(naermest));
      },
      onComplete: () => this.time.delayedCall(180, ankommet),
    });
  }

  private naermesteEtasjeNr(y: number): number {
    let best = BYGNING[0].nr;
    let bestAvstand = Number.POSITIVE_INFINITY;
    for (const e of BYGNING) {
      const d = Math.abs(this.etasjeSenterY(e.nr) - y);
      if (d < bestAvstand) {
        bestAvstand = d;
        best = e.nr;
      }
    }
    return best;
  }

  // ---------- Heismøtet (5B/5D) ----------

  /**
   * Heismøtet (5B): kort dialog i selve heisen. Spilleren velger tone —
   * høflig, spiss eller fiskende. Fisking kan gi hint om rivalens neste
   * trekk, men avsløres du, vektes rival-hendelser opp (5C).
   */
  private visHeismote(type: 'mote' | 'konfrontasjon'): void {
    this.kjorer = true; // blokkér etasjevalg mens dialogen står
    const rival = finnNpc('kapelle-rival');
    const layer = this.add.container(0, 0).setDepth(50);

    const backdrop = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    layer.add(backdrop);

    const sheetX = 70;
    const sheetW = GAME_WIDTH - 140;
    const sheetY = 320;
    const sheetH = 1180;
    const sheet = this.add
      .rectangle(sheetX, sheetY, sheetW, sheetH, 0xf4ead5, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(4, 0x1f2d44, 1);
    layer.add(sheet);

    // Rival-portrett: Higgsfield-tekstur hvis prosessert, ellers initialer.
    const pKey = rival?.portrettKey ?? '';
    if (pKey && this.textures.exists(pKey)) {
      const img = this.add.image(GAME_WIDTH / 2, sheetY + 40, pKey).setOrigin(0.5, 0);
      const tex = this.textures.get(pKey).getSourceImage();
      const h = (tex as HTMLImageElement).height || 1;
      img.setScale(300 / h);
      layer.add(img);
    } else {
      const ring = this.add.circle(GAME_WIDTH / 2, sheetY + 170, 110, 0x2c3a52, 1);
      ring.setStrokeStyle(5, 0xb8923f, 0.9);
      const init = this.add
        .text(GAME_WIDTH / 2, sheetY + 170, initialerAv(rival?.navn ?? 'PW'), {
          fontFamily: FONTS.display,
          fontSize: fontStr(64),
          color: '#f4ead5',
        })
        .setOrigin(0.5);
      layer.add([ring, init]);
    }

    const navn = this.add
      .text(GAME_WIDTH / 2, sheetY + 310, `${rival?.navn ?? 'Rivalen'}\n${rival?.tittel ?? ''}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#1f2d44',
        align: 'center',
        fontStyle: '700',
      })
      .setOrigin(0.5, 0);
    layer.add(navn);

    const replikker =
      type === 'konfrontasjon' ? KONFRONTASJON : HEIS_MOTE_INTRO;
    const dialogTekst = this.add
      .text(sheetX + 44, sheetY + 400, replikker.join('\n\n'), {
        fontFamily: FONTS.body,
        fontSize: fontStr(25),
        color: '#1a1a1a',
        wordWrap: { width: sheetW - 88 },
        lineSpacing: 6,
      })
      .setOrigin(0, 0);
    layer.add(dialogTekst);

    const lukk = (): void => {
      layer.destroy(true);
      this.kjorer = false;
    };

    if (type === 'konfrontasjon') {
      rivalen.markerKonfrontert();
      gameClock.brukTid(TIDSKOSTNADER.samtaleKort, 'konfrontasjon i heisen');
      this.byggDialogKnapp(layer, sheetX + 44, sheetY + sheetH - 130, sheetW - 88, KONFRONTASJON_LUKK_LABEL, lukk);
      return;
    }

    gameClock.brukTid(TIDSKOSTNADER.samtaleKort, 'heismøte med rivalen');
    let knappY = sheetY + sheetH - 3 * 118 - 40;
    for (const valg of HEIS_MOTE_VALG) {
      this.byggDialogKnapp(layer, sheetX + 44, knappY, sheetW - 88, valg.label, () =>
        this.velgTone(valg, dialogTekst, layer, sheetX, sheetY, sheetW, sheetH, lukk),
      );
      knappY += 118;
    }
  }

  /** Spillerens tonevalg: vis svar, påfør effekter, og tilby lukking. */
  private velgTone(
    valg: HeisDialogValg,
    dialogTekst: Phaser.GameObjects.Text,
    layer: Phaser.GameObjects.Container,
    sheetX: number,
    sheetY: number,
    sheetW: number,
    sheetH: number,
    lukk: () => void,
  ): void {
    sfx.play('click');
    // Fjern tone-knappene (alt i layer etter dialogteksten beholdes ikke
    // enkelt — vi tegner et dekkende felt og legger svar + lukk over).
    const dekke = this.add
      .rectangle(sheetX + 4, sheetY + sheetH - 3 * 118 - 44, sheetW - 8, 3 * 118 + 30, 0xf4ead5, 1)
      .setOrigin(0, 0);
    layer.add(dekke);

    let svar = valg.svar.join('\n\n');
    if (valg.tone === 'fiskende') {
      const utfall = rivalen.rullFisking();
      if (utfall === 'hint') {
        const hint =
          FISKENDE_HINTS[Math.floor(Math.random() * FISKENDE_HINTS.length)];
        svar = `${svar}\n\n${hint}`;
      } else {
        rivalen.avslort(gameState.dag);
        svar = `${svar}\n\n${FISKENDE_AVSLORT}`;
      }
    }
    // Erstatt intro-teksten (spilleren har lest den) i stedet for å
    // appende — fiske-payoffen skal aldri klippes bak lukk-knappen.
    dialogTekst.setText(`Du: ${valg.label}\n\n${svar}`);
    this.byggDialogKnapp(
      layer,
      sheetX + 44,
      sheetY + sheetH - 130,
      sheetW - 88,
      'Dørene åpner seg →',
      lukk,
    );
  }

  private byggDialogKnapp(
    layer: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    label: string,
    onClick: () => void,
  ): void {
    const h = 96;
    const bg = this.add
      .rectangle(x, y, w, h, 0x1f2d44, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xb8923f, 0.8);
    const txt = this.add
      .text(x + w / 2, y + h / 2, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(21),
        color: '#f4ead5',
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: w - 40 },
      })
      .setOrigin(0.5);
    layer.add([bg, txt]);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x2c3a52));
    bg.on('pointerout', () => bg.setFillStyle(0x1f2d44));
    bg.on('pointerup', () => {
      haptisk.lett();
      onClick();
    });
  }
}

/** Kjeller vises som «K», resten som tall — som på et ekte norsk heispanel. */
export function etasjeTegn(nr: number): string {
  return nr === 0 ? 'K' : String(nr);
}

function fornavnAv(navn: string): string {
  return navn.split(/\s+/)[0] ?? navn;
}
